import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CLERK_TOKEN_MS = 4000;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" }
});

export function withTimeout(promise, ms, label = "timeout") {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => {
    clearTimeout(timer);
  });
}

// Clerk session JWT getter — registered from AuthProvider (inside ClerkProvider).
let clerkTokenGetter = null;

export function setClerkTokenGetter(fn) {
  clerkTokenGetter = typeof fn === "function" ? fn : null;
}

async function readClerkToken(opts) {
  if (!clerkTokenGetter) return null;
  return withTimeout(clerkTokenGetter(opts), CLERK_TOKEN_MS, "clerk_token_timeout");
}

api.interceptors.request.use(async (config) => {
  const existing = config.headers?.Authorization || config.headers?.authorization;
  if (clerkTokenGetter && !existing) {
    try {
      const clerkToken = await readClerkToken();
      if (clerkToken) {
        config.headers.Authorization = `Bearer ${clerkToken}`;
      }
    } catch {
      /* Clerk not ready, signed out, or token wait timed out */
    }
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status === 401 && !err.config?._retried && clerkTokenGetter) {
      try {
        const fresh = await readClerkToken({ skipCache: true });
        if (fresh && err.config) {
          err.config._retried = true;
          err.config.headers = err.config.headers || {};
          err.config.headers.Authorization = `Bearer ${fresh}`;
          return api.request(err.config);
        }
      } catch { /* fall through */ }
    }
    if (err.response?.status === 401) {
      if (err.config?.skipAuthRedirect) {
        return Promise.reject(err);
      }
      const path = window.location.pathname;
      const PUBLIC_PATHS = new Set([
        "/", "/login", "/sign-up", "/waitlist", "/about", "/pricing", "/contact",
        "/platform", "/whats-new", "/capabilities",
        "/legal/terms", "/legal/risk", "/legal/risk-disclosure", "/legal/privacy", "/legal/refund",
        "/auth-callback",
        "/workspace-app/pending-approval",
      ]);
      if (path.startsWith("/dev/")
          || path.startsWith("/workspace-app/legal/")
          || path.startsWith("/login")
          || path.startsWith("/sign-up")
          || path.startsWith("/waitlist")) {
        return Promise.reject(err);
      }
      if (!PUBLIC_PATHS.has(path)) {
        // Clerk still has a session — don't hard-navigate to /login (that
        // used to dump users onto the dashboard with stale page content).
        let stillSignedIn = false;
        try {
          stillSignedIn = Boolean(await readClerkToken({ skipCache: true }));
        } catch { /* treat as signed out */ }
        if (!stillSignedIn) {
          window.location.href = "/login";
        }
      }
    }
    // P1.6/P1.8 — 402 Payment Required → show the global Upgrade modal.
    // Iter159 — if the response detail indicates a paused trial (not just
    // an upsell nudge), redirect to the dedicated /trial-ended takeover
    // instead of the generic modal.
    if (err.response?.status === 402) {
      const url = err.config?.url || "";
      const detail = err.response?.data?.detail;
      const isPaused = typeof detail === "object" && detail?.code === "trial_paused";
      if (isPaused && window.location.pathname !== "/trial-ended") {
        window.location.href = "/trial-ended";
      } else {
        try {
          window.dispatchEvent(new CustomEvent("markovlab:upgrade-required", {
            detail: { url, detail },
          }));
        } catch {}
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object") {
    if (detail.error) return String(detail.error);
    if (detail.msg) return String(detail.msg);
    if (detail.detail) return formatApiError(detail.detail);
  }
  return String(detail);
}

export default api;
